import { Component, ElementRef, NgZone, OnDestroy, Renderer2, ChangeDetectionStrategy, TemplateRef, ChangeDetectorRef, afterNextRender, input, output, viewChild, effect, inject } from '@angular/core';
import { PlatformService } from '../../../services/platform.service';
import { Subscription } from 'rxjs';
import { VisibilityService } from '../../../services/visibility.service';
import KeenSlider, { KeenSliderInstance, SliderOptions } from 'keen-slider';
import { IntersectionObserverService } from '@ng-web-apis/intersection-observer';
import { UntilDestroy } from 'ngx-unificator/decorator';
import { untilDestroyed } from 'ngx-unificator/rxjs';
import { ScrollService } from '../../../services/scroll.service';
import { DOCUMENT, NgClass } from '@angular/common';
import { WindowService } from 'src/app/core/services/window.service';
import { isMobile } from '../../../signals/device';

export interface PaginationData {
  inited: boolean;
  translateCurrentZeroSlider?: number;
  scrollWidth?: number;
  countViewPagination?: number;
  paginationWrp?: HTMLElement;
  widthPgItem?: number;
  lastScrolledSlide?: number;
  pagination?: HTMLElement;
  pgWidth?: number;
}

export type CustomConfig = {
  autoplay?: boolean;
  delay: number;
  imgLoaded: boolean;
  centered: boolean;
  vertical: boolean;
  slidePagination: boolean;
  isLastBlock: boolean;
  elToScroll: string;
};

export type SliderConfig = Partial<SliderOptions & CustomConfig>;

@UntilDestroy()
@Component({
    selector: 'app-slider',
    templateUrl: './slider.component.html',
    styleUrls: ['./slider.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [IntersectionObserverService],
    imports: [NgClass]
})
export class SliderComponent implements OnDestroy {
  private _platform = inject(PlatformService);
  private _zone = inject(NgZone);
  private _render = inject(Renderer2);
  private _visibility = inject(VisibilityService);
  private _cd = inject(ChangeDetectorRef);
  private _scroll = inject(ScrollService);
  private _window = inject(WindowService);
  private _entries$ = inject<IntersectionObserverService>(IntersectionObserverService);
  private _document = inject<Document>(DOCUMENT);

  public paginationId = Date.now();
  public changeDrag: boolean;

  private _slider: KeenSliderInstance = null;
  private _isWheelUnsubscribed = false;
  private _interval: any = 0;
  private _pause = false;
  private _currentSlide = 0;
  private _startPosition = null;
  private _endPosition = null;
  private _lastSlideIndex: number;
  private _fromTop: number;
  private _slidingPaginationData: PaginationData = {
    inited: false,
    lastScrolledSlide: null,
  };

  private readonly _defaultConfig = {
    root: null,
    rootMargin: '0px',
    threshold: 0.0,
    loop: true,
    slides: {
      perView: 1,
    },
    autoplay: false,
    delay: 5000,
    breakpoints: {
      '(max-width: 720px)': {
        slides: {
          perView: 1,
        },
      },
    },
    imgLoaded: true,
    centered: true,
    vertical: false,
    slidePagination: false,
    renderMode: performance,
    elToScroll: '',
  };

  /**
   * If current browser tab active subscription
   */
  private _pageIsVisible$: Subscription = null;

  /**
   * Slider @viewChild
   */
  protected track = viewChild.required<ElementRef<HTMLElement>>('trackSlider');
  public paginationElement = viewChild.required<TemplateRef<HTMLElement>>('pagination');

  /**
   * Slider @Inputs
   */
  public class = input<string | string[]>('', {
    alias: 'class',
  });

  public slideList = input<any[]>([], {
    alias: 'slideList',
  });

  public config = input<SliderConfig, SliderConfig>(
    {},
    {
      alias: 'config',
      transform: (value: SliderConfig) => {
        return {
          ...this._defaultConfig,
          ...value,
        };
      },
    },
  );

  public intersectionObserver = input<boolean>(true, {
    alias: 'intersectionObserver',
  });

  /**
   * Slider @Outputs event emitters
   */
  public mounted$ = output<any>({ alias: 'mounted' });
  public slideChanged$ = output<any>({ alias: 'slideChanged' });
  public created$ = output<any>({ alias: 'created' });
  public updated$ = output<any>({ alias: 'updated' });
  public animationEnded$ = output<any>({ alias: 'animationEnded' });

  constructor() {
    afterNextRender(() => {
      this._runSlider();
    });
    effect(() => {
      if (this._slider) {
        this._destroySlider();
        setTimeout(() => {
          this._addSlideItemClass();
          this._initSlider();
          this._runSliderEmitters();
        }, 200);
      }
    });
  }

  public get slider(): KeenSliderInstance {
    return this._slider;
  }

  public get currentSlide() {
    return this._currentSlide;
  }

  public get isDragFromBottomToTop() {
    return this._startPosition > this._endPosition;
  }

  private _runSlider() {
    if (this._platform.isBrowser) {
      !isMobile() ? (this._fromTop = 380) : (this._fromTop = 200);
      this._zone.run(() => {
        try {
          if (this.intersectionObserver()) {
            this._entries$.pipe(untilDestroyed(this)).subscribe(e => {
              if (e[0].isIntersecting) {
                this._addSlideItemClass();
                this._initSlider();
                this._runSliderEmitters();
              } else {
                this._destroySlider();
              }
            });
          } else {
            this._addSlideItemClass();
            this._initSlider();
            this._runSliderEmitters();
          }
        } catch (error) {
          this._addSlideItemClass();
          this._initSlider();
          this._runSliderEmitters();
        }
      });
    }
  }

  /**
   * Initialize slider
   */
  private _initSlider() {
    this._slider = new KeenSlider(
      this.track().nativeElement,
      {
        ...this.config(),
        created: slider => {
          this.mounted$?.emit(slider);
          this._currentSlide = slider.track?.details?.rel;
          this.slideChanged$?.emit(this._currentSlide);
          this.created$?.emit(slider);
          this._lastSlideIndex = slider.track?.details?.slidesLength - 1;
          this._runDetectChanges();
          if (this.config().defaultAnimation) {
            slider.moveToIdx(5, true, this.config().defaultAnimation);
          }
        },
        slideChanged: slider => {
          this._currentSlide = slider.track?.details?.rel;
          this.slideChanged$?.emit(this._currentSlide);
          this.updated$?.emit(slider);
          this._endPosition = slider.track?.details?.position;
          this._runDetectChanges();
          this._scrollingPositionDots();
        },
        animationEnded: slider => {
          this.animationEnded$?.emit(slider);
          if (this.config().defaultAnimation) {
            slider.moveToIdx(slider.track.details.abs + 5, true, this.config().defaultAnimation);
          }
        },
        dragStarted: slider => {
          this._startPosition = slider.track?.details?.position;
          this.setPause(true);
        },
        dragEnded: () => {
          this.setPause(false);
        },
        updated: slider => {
          if (this.config().defaultAnimation) {
            slider.moveToIdx(slider.track.details.abs + 5, true, this.config().defaultAnimation);
          }
        },
      },
    );
    this._runDetectChanges();
  }

  /**
   * Run slider emitters
   */
  private _runSliderEmitters() {
    if (this._slider) {
      this._addImgClassLoaded();
      this._visibilityEmiter();
      this._prepareForPaginationSlider();
    }
  }

  /**
   * Add class on slide element before slider init
   */
  private _addSlideItemClass() {
    this.track().nativeElement.childNodes.forEach((slide: any) => {
      if (slide.nodeType !== Node.COMMENT_NODE) {
        this._render.addClass(slide, 'keen-slider__slide');
      }
    });
  }

  /**
   * Add image class loaded
   */
  private _addImgClassLoaded() {
    if (this.config().imgLoaded) {
      this.track()
        .nativeElement.querySelectorAll('img')
        .forEach((img: HTMLImageElement) => this._render.addClass(img, 'loaded'));
    }
  }

  /**
   * Run slider autoplay and mouse events
   */
  private _setAutoPlay() {
    this._resetInterval();
    this._interval = setInterval(() => {
      if (!this._pause) {
        this.slider.next();
      }
    }, this.config().delay);
  }

  /**
   * Set slider autoplay state
   * @param active
   */
  public setPause(active: boolean) {
    if (this.config().autoplay) {
      this._pause = active;
      this._setAutoPlay();
    }
  }

  /**
   * Reset slider interval
   */
  private _resetInterval() {
    clearInterval(this._interval);
  }

  /**
   * If current tab in browser not active, it set pause slider autoplay
   */
  private _visibilityEmiter() {
    if (this.config().autoplay) {
      this._pageIsVisible$ = this._visibility.pageIsVisible$.subscribe(e => {
        e ? this.setPause(false) : this.setPause(true);
      });
    }
  }

  /**
   * Destroy slider
   */
  private _destroySlider() {
    if (this._platform.isBrowser) {
      this._scroll.UnblockScroll();
      if (this._pageIsVisible$) {
        this._pageIsVisible$.unsubscribe();
      }
      if (this._slider && this._slider?.slides?.length) {
        this._slider.destroy();
      }
    }
  }

  /**
   * Run manual detect changes
   */
  private _runDetectChanges() {
    try {
      if (this._slider || this.track()) {
        this._cd.detectChanges();
        this.track().nativeElement.click();
      }
    } catch (error) {
    }
  }

  ngOnDestroy() {
    this._destroySlider();
  }

  /**
   * Scrolling position dots after slider changed
   * @private
   */
  private _scrollingPositionDots() {
    if (
      this._platform.isBrowser &&
      this.config().slidePagination &&
      this._slidingPaginationData.inited &&
      this._slidingPaginationData.lastScrolledSlide !== this._currentSlide
    ) {
      this._slidingPaginationData.lastScrolledSlide = this._currentSlide;
      const translate =
        this.currentSlide + 1 <= Math.ceil(this._slidingPaginationData.countViewPagination)
          ? this._slidingPaginationData.translateCurrentZeroSlider
          : this.currentSlide + 1 >= this.slideList().length - Math.ceil(this._slidingPaginationData.countViewPagination)
            ? -this._slidingPaginationData.translateCurrentZeroSlider
            : this._slidingPaginationData.translateCurrentZeroSlider -
            (this._slidingPaginationData.widthPgItem / this._slidingPaginationData.scrollWidth) * 100 * (this.currentSlide - 2);
      this._render.setStyle(this._slidingPaginationData.paginationWrp, 'transform', `translateX(${translate}%)`);
      setTimeout(() => {
        this._render.setStyle(this._slidingPaginationData.pagination, 'width', `${this._slidingPaginationData.pgWidth}px`);
      }, 100);
    }
  }

  /**
   * Prepare data for pagination scrolling (calculate only one tine)
   * @private
   */
  private _prepareForPaginationSlider() {
    if (this._platform.isBrowser && this.config().slidePagination) {
      setTimeout(() => {
        const pagination: HTMLElement = this._document.getElementById(this.paginationId.toString());
        const paginationWrp: HTMLElement = pagination?.querySelector('.pagination__wrapper');
        const pgItem: HTMLElement = pagination?.querySelector('.pagination__item:nth-child(1)');
        const marginRightPgItem = this._window?.nativeWindow?.getComputedStyle(pgItem)?.marginRight;
        const widthPgItem = pgItem?.getBoundingClientRect()?.width + parseFloat(marginRightPgItem);
        const countViewPagination = Math.floor(pagination?.getBoundingClientRect()?.width / widthPgItem);
        if (this.slideList().length <= countViewPagination) {
          return;
        }
        const pgWidth = pagination?.getBoundingClientRect().width;
        const scrollWidth = paginationWrp?.getBoundingClientRect()?.width;
        const translateCurrentZeroSlider = (100 - (pgWidth / scrollWidth) * 100) / 2;
        this._slidingPaginationData = {
          inited: true,
          lastScrolledSlide: null,
          translateCurrentZeroSlider,
          scrollWidth,
          countViewPagination,
          paginationWrp,
          widthPgItem,
          pagination,
          pgWidth,
        };
        this._scrollingPositionDots();
      }, 1000);
    }
  }
}
